import { HelpBtn } from './HelpBtn';
import MaskToggle from './MaskToggle';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Bluetooth, Usb, Wifi, Smartphone, Plug, Unplug, MapPinPlus, MapPinned } from 'lucide-react';
import { HRMS_OPTS, PROFILES } from '../lib/precision';
import ControlPoints from './ControlPoints';
import { useGnss, getGnssCfg, setGnssCfg, startGnss, stopGnss, gnssSupport, BAUDS, DEVICES, FIX_COLORS, gnssLive } from '../lib/gnss';

export const fmtAcc = (v) => (v == null ? '–' : v < 1 ? `${(v * 100).toFixed(1)} cm` : `${v.toFixed(2)} m`);
const MODES = [['system', Smartphone], ['ble', Bluetooth], ['serial', Usb], ['ws', Wifi]];

export function GnssTelemetry({ t, s }) {
  const cfg = getGnssCfg();
  const fix = s.connected ? s.fix || 'none' : 'none';
  const ground = s.alt != null ? s.alt - (+cfg.antenna || 0) : null;
  const cells = [
    [t.gnssSats, s.sats != null ? `${s.sats}${s.systems.length ? ' – ' + s.systems.join('/') : ''}` : '–', 'sats'],
    [t.gnssAcc, `H ${fmtAcc(s.hrms)} · V ${fmtAcc(s.vrms)}${s.hdop != null ? ` · HDOP ${s.hdop}` : ''}`, 'acc'],
    [t.gnssAlt, s.alt != null ? `${s.alt.toFixed(3)} m${s.geoid != null ? ` (${t.gnssGeoid} ${s.geoid.toFixed(2)} m)` : ''}` : '–', 'alt'],
    [t.gnssGround, ground != null ? `${ground.toFixed(3)} m ${t.gnssNN}` : '–', 'ground'],
    [t.gnssCoords, s.lat != null ? `${s.lat.toFixed(8)}, ${s.lon.toFixed(8)}` : '–', 'coords'],
  ];
  return (
    <div className="gnss-tele" data-testid="gnss-telemetry">
      <div className="gnss-fix" style={{ '--fix': FIX_COLORS[fix] }} data-testid="gnss-fix-status"><span className="gnss-fix-val"><i />{t['gnssFix_' + fix]}</span>{s.est && s.connected ? <small>({t.gnssEst})</small> : null}{s.sim && s.connected ? <small className="gnss-sim-tag" data-testid="gnss-sim-tag">SIMULATOR</small> : null}</div>
      {cells.map(([k, v, id]) => <div key={id} className="gnss-cell"><small>{k}</small><b data-testid={`gnss-tele-${id}`}>{v}</b></div>)}
      {s.error && <p className="gnss-err" data-testid="gnss-error">{s.error}</p>}
    </div>
  );
}

function Settings({ t, cfg, set }) {
  const num = (k, min, max, step) => <input type="number" min={min} max={max} step={step} data-testid={`gnss-${k}-input`} value={cfg[k]} onChange={(e) => set({ [k]: e.target.value === '' ? '' : Math.min(max, Math.max(min, +e.target.value)) })} />;
  return (
    <div className="gnss-grid">
      <label>{t.gnssDevice}
        <input list="gnss-devices" data-testid="gnss-device-input" value={cfg.device} onChange={(e) => set({ device: e.target.value })} />
        <datalist id="gnss-devices">{[...DEVICES, t.gnssOther].map((d) => <option key={d} value={d} />)}</datalist>
      </label>
      <label>{t.gnssAntenna}{num('antenna', 0, 5, 0.01)}</label>
      <label>{t.gnssDepth}{num('depth', 0, 10, 0.01)}</label>
      <label>{t.gnssAvg}
        <select data-testid="gnss-avg-select" value={cfg.avg} onChange={(e) => set({ avg: +e.target.value })}>
          {[1, 5, 10].map((n) => <option key={n} value={n}>{t['gnssAvg_' + n]}</option>)}
        </select>
      </label>
      <label>{t.prHrms}
        <select data-testid="gnss-hrms-select" value={cfg.hrmsMax} onChange={(e) => set({ hrmsMax: +e.target.value })}>
          {HRMS_OPTS.map((n) => <option key={n} value={n}>{`≤ ${n.toFixed(2).replace('.', ',')} m`}</option>)}
        </select>
      </label>
      <label>{t.prMode}
        <span className="chip-group">{PROFILES.map((m) => <button key={m} type="button" className={`chip seg ${cfg.prec === m ? 'on' : ''}`} data-testid={`gnss-prec-${m}`} onClick={() => set({ prec: m })}>{t['prMode_' + m]}</button>)}</span>
        <small className="muted" data-testid="gnss-prec-hint">{t['prModeD_' + cfg.prec]}</small>
      </label>
      <label>{t.gnssMaxAge}{num('maxAge', 1, 10, 1)}</label>
      <label>{t.gnssPoleRef}
        <span className="chip-group">{['antenna', 'tip'].map((m) => <button key={m} type="button" className={`chip seg ${(cfg.poleRef || 'antenna') === m ? 'on' : ''}`} data-testid={`gnss-poleref-${m}`} onClick={() => set({ poleRef: m })}>{t['gnssPoleRef_' + m]}</button>)}</span>
        <small className="muted">{t.gnssPoleRefD}</small>
      </label>
    </div>
  );
}

export default function GnssDialog({ t, center, onPlace, project, updateProject }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(getGnssCfg);
  const [busy, setBusy] = useState(false);
  const s = useGnss();
  const sup = gnssSupport();
  useEffect(() => {
    const on = () => { setCfg(getGnssCfg()); setOpen(true); };
    window.addEventListener('giss-gnss-open', on);
    return () => window.removeEventListener('giss-gnss-open', on);
  }, []);
  if (!open) return null;
  const set = (p) => setCfg(setGnssCfg(p));
  const connect = async () => {
    if (!sup[cfg.mode]) return toast.error(t.gnssUnsupported);
    setBusy(true);
    const ok = await startGnss(cfg, center());
    setBusy(false);
    ok ? toast.success(t.gnssConnected) : toast.error(t.gnssConnErr);
  };
  return (
    <div className="editor-backdrop gnss-backdrop" data-testid="gnss-dialog">
      <div className="gnss-modal mask">
        <div className="gnss-head mask-keep">
          <b data-testid="gnss-title">📡 {t.gnssTitle}</b>
          <MaskToggle t={t} id="gnss-dialog" /><HelpBtn k="n:meas_accuracy" testid="gnss-help" title={t.helpHere} /><button className="icon-btn" data-testid="gnss-close-btn" onClick={() => setOpen(false)}><X size={16} /></button>
        </div>
        <GnssTelemetry t={t} s={s} />
        {gnssLive(s) && (
          <div className="row">
            <button className="btn btn-accent btn-sm" data-testid="gnss-dialog-place-fit-btn" onClick={() => { setOpen(false); onPlace('fit'); }}><MapPinPlus size={14} /> {t.gnssPlaceFit}</button>
            <button className="btn btn-primary btn-sm" data-testid="gnss-dialog-place-rep-btn" onClick={() => { setOpen(false); onPlace('rep'); }}><MapPinned size={14} /> {t.gnssPlaceRep}</button>
          </div>
        )}
        <h5>{t.gnssConn}</h5>
        <div className="gnss-modes">
          {MODES.map(([m, Icon]) => (
            <button key={m} className={`gnss-mode ${cfg.mode === m ? 'on' : ''}`} data-testid={`gnss-mode-${m}`} disabled={s.connected} onClick={() => set({ mode: m })}>
              <Icon size={17} /><span>{t['gnssMode_' + m]}</span>{!sup[m] && <em>{t.gnssUnsupported}</em>}
            </button>
          ))}
        </div>
        {cfg.mode === 'serial' && (
          <label className="gnss-inline">{t.gnssBaud}
            <select data-testid="gnss-baud-select" value={cfg.baud} disabled={s.connected} onChange={(e) => set({ baud: +e.target.value })}>{BAUDS.map((b) => <option key={b} value={b}>{b}</option>)}</select>
          </label>
        )}
        {cfg.mode === 'ws' && (
          <div className="gnss-inline">
            <input data-testid="gnss-url-input" value={cfg.url} disabled={s.connected || cfg.sim} placeholder="ws://192.168.42.1:9001" onChange={(e) => set({ url: e.target.value })} />
            <label className="gnss-check"><input type="checkbox" data-testid="gnss-sim-checkbox" checked={!!cfg.sim} disabled={s.connected} onChange={(e) => set({ sim: e.target.checked })} /> {t.gnssSim}</label>
          </div>
        )}
        <p className="gnss-hint">{t['gnssHint_' + cfg.mode]}</p>
        {s.connected
          ? <button className="btn btn-red" data-testid="gnss-disconnect-btn" onClick={stopGnss}><Unplug size={16} /> {t.gnssDisconnect}</button>
          : <button className="btn btn-green" data-testid="gnss-connect-btn" disabled={busy} onClick={connect}><Plug size={16} /> {t.gnssConnect}</button>}
        {project && <ControlPoints t={t} project={project} updateProject={updateProject} />}
        <h5>{t.gnssSettings}</h5>
        <Settings t={t} cfg={cfg} set={set} />
      </div>
    </div>
  );
}
